import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { cn } from 'src/lib/utils';
import { portalDs } from 'src/components/portal-design-system';

interface QuotaOverrideEditorProps {
  value: number | null;
  disabled?: boolean;
  onSave: (value: number | null) => Promise<{ ok: boolean; message?: string }>;
}

export function QuotaOverrideEditor({
  value,
  disabled = false,
  onSave,
}: QuotaOverrideEditorProps) {
  const [draft, setDraft] = useState(value == null ? '' : String(value));
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setDraft(value == null ? '' : String(value));
  }, [value]);

  const handleSave = async() => {
    setInlineError(null);
    const trimmed = draft.trim();

    if (trimmed === '') {
      setIsSaving(true);
      const result = await onSave(null);
      setIsSaving(false);
      if (!result.ok) {
        setInlineError(result.message ?? 'Failed to clear creative quota.');
      }
      return;
    }

    const parsedValue = Number(trimmed);
    if (!Number.isInteger(parsedValue) || parsedValue < 0) {
      setInlineError('Creative quota must be a whole number of 0 or greater.');
      return;
    }

    setIsSaving(true);
    const result = await onSave(parsedValue);
    setIsSaving(false);

    if (!result.ok) {
      setInlineError(result.message ?? 'Failed to save creative quota.');
    }
  };

  const handleClear = async() => {
    setDraft('');
    setInlineError(null);
    setIsSaving(true);
    const result = await onSave(null);
    setIsSaving(false);
    if (!result.ok) {
      setInlineError(result.message ?? 'Failed to clear creative quota.');
    }
  };

  return (
    <div className="flex min-w-[180px] flex-col gap-1.5">
      <div className="flex items-center gap-1.5">
        <input
          type="number"
          min={0}
          step={1}
          value={draft}
          disabled={disabled || isSaving}
          onChange={(event) => setDraft(event.target.value)}
          placeholder="No override"
          aria-label="Creative quota"
          className="h-8 w-[88px] rounded-lg border border-[#D9DBE3] px-2 text-[12px] text-[#0D0F1A] focus:border-[#0D0F1A] focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
        />
        <button
          type="button"
          disabled={disabled || isSaving}
          onClick={() => void handleSave()}
          className="inline-flex h-8 items-center rounded-lg border border-[#D9DBE3] px-2 text-[12px] font-semibold text-[#0D0F1A] transition-colors hover:bg-[#F0F1F5] disabled:cursor-not-allowed disabled:opacity-50"
        >
          Save
        </button>
        <button
          type="button"
          disabled={disabled || isSaving || value == null}
          onClick={() => void handleClear()}
          className="inline-flex size-8 items-center justify-center rounded-lg border border-[#D9DBE3] text-[#5E6278] transition-colors hover:bg-[#F0F1F5] disabled:cursor-not-allowed disabled:opacity-50"
          aria-label="Clear creative quota override"
        >
          <X className="size-3.5" />
        </button>
      </div>
      {inlineError ? (
        <p className={cn(portalDs.caption, 'text-[#DC2626]')}>
          {inlineError}
        </p>
      ) : null}
    </div>
  );
}
